import { useHotel } from "@/hooks/useHotel";
import { pmsService } from "@/services/pms-service";
import {
  PmsSettings,
  PmsProviderOption,
  CredentialsStatus,
  UpdatePmsCredentialsRequest,
  UpdatePmsSettingsRequest,
  EnablePmsRequest,
  TestConnectionResponse,
  SyncJob,
  SyncEntityType,
  SyncStats,
  RetryStats,
  ReconciliationStatus,
  SyncJobStatus,
  SyncJobsFilters,
  SyncJobsExportParams,
  PaginatedSyncJobs,
} from "@/types/pms";
import { is404 } from "@/utils/httpErrors";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const pmsKeys = {
  all: ["pms"] as const,
  providers: () => [...pmsKeys.all, "providers"] as const,
  settings: (hotelId: string) => [...pmsKeys.all, "settings", hotelId] as const,
  credentials: (hotelId: string) =>
    [...pmsKeys.all, "credentials", hotelId] as const,
  syncJobsAll: (hotelId: string) =>
    [...pmsKeys.all, "syncJobs", hotelId] as const,
  syncJobs: (hotelId: string, filters?: SyncJobsFilters) =>
    [...pmsKeys.all, "syncJobs", hotelId, filters ?? {}] as const,
  syncJob: (hotelId: string, jobId: string) =>
    [...pmsKeys.all, "syncJob", hotelId, jobId] as const,
  syncStats: (hotelId: string) =>
    [...pmsKeys.all, "syncStats", hotelId] as const,
  retryStats: (hotelId: string) =>
    [...pmsKeys.all, "retryStats", hotelId] as const,
  reconciliation: (hotelId: string) =>
    [...pmsKeys.all, "reconciliation", hotelId] as const,
};

const useResolvedHotelId = (hotelIdOverride?: string | null) => {
  const { hotelId } = useHotel(hotelIdOverride === undefined);

  return hotelIdOverride !== undefined ? hotelIdOverride : hotelId;
};

export const useGetPmsProviders = () => {
  return useQuery<PmsProviderOption[]>({
    queryKey: pmsKeys.providers(),
    queryFn: () => pmsService.getProviders(),
    staleTime: 30 * 60 * 1000,
  });
};

export const useGetPmsSettings = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useQuery<PmsSettings | null>({
    queryKey: pmsKeys.settings(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await pmsService.getSettings(hotelId!);
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useGetCredentialsStatus = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useQuery<CredentialsStatus | null>({
    queryKey: pmsKeys.credentials(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await pmsService.getCredentialsStatus(hotelId!);
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useUpdatePmsCredentials = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<void, unknown, UpdatePmsCredentialsRequest>({
    mutationFn: async (data) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.updateCredentials(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.credentials(hotelId) });
      qc.invalidateQueries({ queryKey: pmsKeys.settings(hotelId) });
    },
  });
};

export const useDeletePmsCredentials = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.deleteCredentials(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.credentials(hotelId) });
      qc.invalidateQueries({ queryKey: pmsKeys.settings(hotelId) });
    },
  });
};

export const useTestPmsConnection = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useMutation<TestConnectionResponse, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.testConnection(hotelId);
    },
  });
};

export const useEnablePms = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<PmsSettings, unknown, EnablePmsRequest>({
    mutationFn: async (data) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.enable(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.settings(hotelId) });
      qc.invalidateQueries({ queryKey: pmsKeys.credentials(hotelId) });
    },
  });
};

export const useDisablePms = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<PmsSettings, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.disable(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.settings(hotelId) });
      qc.invalidateQueries({ queryKey: pmsKeys.credentials(hotelId) });
    },
  });
};

export const useUpdatePmsSettings = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<PmsSettings, unknown, UpdatePmsSettingsRequest>({
    mutationFn: async (data) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.updateSettings(hotelId, data);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.settings(hotelId) });
    },
  });
};

export const useSyncJobs = (
  filters?: SyncJobsFilters,
  hotelIdOverride?: string | null,
) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useQuery<PaginatedSyncJobs>({
    queryKey: pmsKeys.syncJobs(hotelId || "", filters),
    enabled: !!hotelId,
    queryFn: () => pmsService.getSyncJobs(hotelId!, filters),
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      hasRunningJobs(query.state.data?.items ?? []) ? 5000 : false,
  });
};

export const useSyncJobDetail = (
  jobId: string,
  hotelIdOverride?: string | null,
) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useQuery<SyncJob>({
    queryKey: pmsKeys.syncJob(hotelId || "", jobId),
    enabled: !!hotelId && !!jobId,
    queryFn: () => pmsService.getSyncJob(hotelId!, jobId),
    staleTime: 5 * 60 * 1000,
  });
};

export const useTriggerFullSync = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<SyncJob, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.triggerFullSync(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.syncJobsAll(hotelId) });
    },
  });
};

export const useTriggerSelectiveSync = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<SyncJob, unknown, SyncEntityType[]>({
    mutationFn: async (entities) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.triggerSelectiveSync(hotelId, entities);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.syncJobsAll(hotelId) });
    },
  });
};

export const useTriggerIncrementalSync = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);
  const qc = useQueryClient();

  return useMutation<SyncJob, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.triggerIncrementalSync(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.syncJobsAll(hotelId) });
    },
  });
};

export const useCancelSyncJob = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<SyncJob, unknown, string>({
    mutationFn: async (jobId) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.cancelSyncJob(hotelId, jobId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.syncJobsAll(hotelId) });
    },
  });
};

export const useGetSyncStats = (hotelIdOverride?: string | null) => {
  const hotelId = useResolvedHotelId(hotelIdOverride);

  return useQuery<SyncStats | null>({
    queryKey: pmsKeys.syncStats(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        return await pmsService.getSyncStats(hotelId!);
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useGetRetryStats = () => {
  const { hotelId } = useHotel();

  return useQuery<RetryStats>({
    queryKey: pmsKeys.retryStats(hotelId || ""),
    enabled: !!hotelId,
    queryFn: () => pmsService.getRetryStats(hotelId!),
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetReconciliationStatus = () => {
  const { hotelId } = useHotel();

  return useQuery<ReconciliationStatus | null>({
    queryKey: pmsKeys.reconciliation(hotelId || ""),
    enabled: !!hotelId,
    queryFn: async () => {
      try {
        // The endpoint answers with an empty body when the connector has no
        // reconciliation data; BaseService turns that into `undefined`, which
        // TanStack Query rejects as an error and retries — normalize to null
        // so the page simply omits the card.
        return (await pmsService.getReconciliationStatus(hotelId!)) ?? null;
      } catch (e) {
        if (is404(e)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !is404(error) && failureCount < 2,
  });
};

export const useTriggerReconciliation = () => {
  const { hotelId } = useHotel();
  const qc = useQueryClient();

  return useMutation<void, unknown, void>({
    mutationFn: async () => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.triggerReconciliation(hotelId);
    },
    onSuccess: () => {
      if (!hotelId) return;
      qc.invalidateQueries({ queryKey: pmsKeys.reconciliation(hotelId) });
      qc.invalidateQueries({ queryKey: pmsKeys.syncStats(hotelId) });
    },
  });
};

export const useExportSyncJobs = () => {
  const { hotelId } = useHotel();

  return useMutation<Blob, unknown, SyncJobsExportParams>({
    mutationFn: async (params) => {
      if (!hotelId) throw new Error("hotelId is required");
      return pmsService.exportSyncJobs(hotelId, params);
    },
  });
};

export const hasRunningJobs = (jobs: SyncJob[]): boolean =>
  jobs.some(
    (j) =>
      j.status === SyncJobStatus.PENDING || j.status === SyncJobStatus.RUNNING,
  );
